/**
 * Central audit viewer — request schemas, wire types and the page cursor
 * (docs/AUDIT.md §10a).
 *
 *   GET /api/v1/audit/orgs/:orgId/events            (org admins)
 *       → pages `org_audit_change_sets`, keyset ("timestamp" DESC,
 *         change_set_key DESC) — `AuditEventsResponse`
 *   GET /api/v1/audit/orgs/:orgId/change-sets?timestamp=&key=   (org admins)
 *       → the `org_audit_events` rows of one change set — `AuditChangeSetResponse`
 *   GET /api/v1/audit/orgs/:orgId/records/:recordType/:recordId  (members)
 *       → one record's history — `AuditRecordHistoryResponse`
 *   GET /api/v1/audit/platform/records/:recordType/:recordId  (super admins)
 *       → one platform record's history (no org) — `AuditRecordHistoryResponse`
 *   GET /api/v1/audit/orgs/:orgId/activity          (org admins)
 *       → change sets per org-local day over a 30/90/365-day window —
 *         `AuditActivityResponse`
 *   GET /api/v1/audit/orgs/:orgId/search?q=         (org admins)
 *       → records found by number, name or description, deleted ones
 *         included — `AuditSearchResponse`
 *
 * `source` values are registry keys: the audit table names in
 * `AUDIT_SOURCE_TABLES` (`@releve/shared` audit registry).
 */
import { z } from "zod";
import { AUDIT_RECORD_TYPES, AUDIT_ROOT_TYPES, PLATFORM_AUDIT_RECORD_TYPES, isAuditSourceTable, } from "../audit/registry.js";
import { calendarDateSchema } from "./dateWindow.js";
// ---------------------------------------------------------------------------
// Query schemas
// ---------------------------------------------------------------------------
/** Longest `change_set_key` the server accepts (a request id, or `root_type:uuid`). */
export const AUDIT_CHANGE_SET_KEY_MAX = 200;
/**
 * Longest cursor string. Sized so a cursor carrying a maximum-length key still
 * round-trips: base64url of `{"ts":"<≤35>","key":"<≤200>"}` is ≤ 336 chars.
 */
export const AUDIT_CURSOR_MAX = 400;
/**
 * A `timestamptz` as PostgREST prints it (`2026-09-29T04:38:12.123456+00:00`),
 * also accepting `Z` and a space separator. Checked before the value is put in
 * a PostgREST `.or()` / `.lte()` filter.
 */
export const AUDIT_TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}(\.\d{1,6})?(Z|[+-]\d{2}(:?\d{2})?)$/;
/** Upper-case verb (`INSERT`, `DETACH`, `ATTACHMENT_UPDATE`, …) — safe inside `cs {…}`. */
const AUDIT_ACTION_RE = /^[A-Z][A-Z_]{0,39}$/;
// No control characters in a key that ends up quoted inside a filter string.
// eslint-disable-next-line no-control-regex
const CONTROL_CHAR_RE = /[\u0000-\u001f\u007f]/;
export const auditChangeSetKeySchema = z
    .string()
    .min(1)
    .max(AUDIT_CHANGE_SET_KEY_MAX)
    .refine((v) => !CONTROL_CHAR_RE.test(v), { message: "Invalid change set key" });
export const auditTimestampSchema = z.string().regex(AUDIT_TIMESTAMP_RE, "Invalid timestamp");
/** `"true"` / `"false"` query strings (z.coerce.boolean would read "false" as true). */
const queryBoolean = z
    .union([z.boolean(), z.enum(["true", "false", "1", "0"])])
    .transform((v) => v === true || v === "true" || v === "1");
/** Earliest calendar day a date filter accepts (no audit row predates it). */
export const AUDIT_MIN_DAY = "1970-01-01";
/** A `YYYY-MM-DD` filter day on or after {@link AUDIT_MIN_DAY}. */
const auditFilterDaySchema = calendarDateSchema.refine((v) => v >= AUDIT_MIN_DAY, {
    message: `must not be before ${AUDIT_MIN_DAY}`,
});
/**
 * The filters `/events` and `/activity` share, as a RAW shape (not a
 * `z.object`) so each query schema can spread it: a schema that ends in
 * `.refine` is a `ZodEffects` and cannot be `.extend`ed.
 *
 * `source` is a comma-separated list of registry keys; an unknown key is a
 * 400. `action` matches the set's ONE verb (`primary_action`), the same verb
 * the log's Action column shows. `rootType` + `rootId` narrow to one record
 * (the view's `root_keys` holds `<root_type>:<root_id>`); they come as a
 * pair — see {@link hasAuditRootPair}.
 */
export const auditBaseFilterShape = {
    source: z
        .string()
        .max(2000)
        .optional()
        .transform((v) => v === undefined
        ? undefined
        : v
            .split(",")
            .map((s) => s.trim())
            .filter((s) => s !== ""))
        .superRefine((list, ctx) => {
        for (const s of list ?? []) {
            if (!isAuditSourceTable(s)) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message: `Unknown audit source: ${s}` });
            }
        }
    })
        .transform((list) => (list && list.length > 0 ? list : undefined)),
    action: z.string().regex(AUDIT_ACTION_RE, "Invalid action").optional(),
    includeBaseline: queryBoolean.optional().default(false),
    userId: z.string().uuid().optional(),
    rootType: z.enum(AUDIT_ROOT_TYPES).optional(),
    rootId: z.string().uuid().optional(),
};
/** `rootType` and `rootId` are both present or both absent. */
function hasAuditRootPair(q) {
    return (q.rootType === undefined) === (q.rootId === undefined);
}
const ROOT_PAIR_ISSUE = { message: "rootType and rootId go together", path: ["rootId"] };
/**
 * GET …/events query: the shared filters plus a date window, the cursor and
 * the page size.
 *
 * `from` / `to` are calendar days (`YYYY-MM-DD`), both inclusive, not before
 * 1970-01-01, read in the ORGANIZATION's timezone: the server resolves them to
 * `timestamptz` bounds (`zonedDayStartInstant(from)` ≤ timestamp <
 * `zonedDayStartInstant(to + 1)`) with `organizations.timezone`, so a day
 * means the same thing for every viewer whatever their browser zone
 * (docs/DATES.md).
 */
export const auditEventsQuerySchema = z
    .object({
    ...auditBaseFilterShape,
    from: auditFilterDaySchema.optional(),
    to: auditFilterDaySchema.optional(),
    cursor: z.string().min(1).max(AUDIT_CURSOR_MAX).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional().default(50),
})
    .refine((q) => !q.from || !q.to || q.from <= q.to, {
    message: "from must not be after to",
    path: ["to"],
})
    .refine(hasAuditRootPair, ROOT_PAIR_ISSUE);
/** Chart windows `/activity` accepts, in days (the RPC's `p_days`). */
export const AUDIT_ACTIVITY_RANGES = ["30", "90", "365"];
/**
 * GET …/activity query: the shared filters plus the chart window. The window
 * always ends on the org's today; `range` is its length in days.
 */
export const auditActivityQuerySchema = z
    .object({
    ...auditBaseFilterShape,
    range: z.enum(AUDIT_ACTIVITY_RANGES).default("90"),
})
    .refine(hasAuditRootPair, ROOT_PAIR_ISSUE);
/** Most results `/search` returns (the RPC's cap). */
export const AUDIT_SEARCH_LIMIT_MAX = 20;
/**
 * GET …/search query: find records by number, name or description, deleted
 * ones included. `q` is trimmed, 2–100 characters, no control characters.
 */
export const auditSearchQuerySchema = z.object({
    q: z
        .string()
        .trim()
        .min(2)
        .max(100)
        .refine((v) => !CONTROL_CHAR_RE.test(v), { message: "Invalid search text" }),
    limit: z.coerce.number().int().min(1).max(AUDIT_SEARCH_LIMIT_MAX).optional().default(AUDIT_SEARCH_LIMIT_MAX),
});
/** GET …/change-sets query — one change set, the lazy expand of an org-log row. */
export const auditChangeSetQuerySchema = z.object({
    timestamp: auditTimestampSchema,
    key: auditChangeSetKeySchema,
});
export const auditRecordTypeSchema = z.enum(AUDIT_RECORD_TYPES);
/** GET …/records/:recordType/:recordId path params. */
export const auditRecordParamsSchema = z.object({
    recordType: auditRecordTypeSchema,
    recordId: z.string().uuid(),
});
/** GET /api/v1/audit/platform/records/:recordType/:recordId path params. */
export const platformAuditRecordParamsSchema = z.object({
    recordType: z.enum(PLATFORM_AUDIT_RECORD_TYPES),
    recordId: z.string().uuid(),
});
const auditCursorSchema = z.object({ ts: auditTimestampSchema, key: auditChangeSetKeySchema }).strict();
const B64URL = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
function utf8Encode(s) {
    const bytes = [];
    for (const ch of s) {
        const cp = ch.codePointAt(0);
        if (cp < 0x80)
            bytes.push(cp);
        else if (cp < 0x800)
            bytes.push(0xc0 | (cp >> 6), 0x80 | (cp & 63));
        else if (cp < 0x10000)
            bytes.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
        else
            bytes.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 63), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
    }
    return bytes;
}
function utf8Decode(bytes) {
    let out = "";
    for (let i = 0; i < bytes.length;) {
        const b = bytes[i];
        let cp;
        let extra;
        if (b < 0x80) {
            cp = b;
            extra = 0;
        }
        else if (b >= 0xc2 && b < 0xe0) {
            cp = b & 31;
            extra = 1;
        }
        else if (b >= 0xe0 && b < 0xf0) {
            cp = b & 15;
            extra = 2;
        }
        else if (b >= 0xf0 && b < 0xf5) {
            cp = b & 7;
            extra = 3;
        }
        else
            return null;
        if (i + extra >= bytes.length)
            return null;
        for (let k = 1; k <= extra; k++) {
            const c = bytes[i + k];
            if (c === undefined || (c & 0xc0) !== 0x80)
                return null;
            cp = (cp << 6) | (c & 63);
        }
        if (cp > 0x10ffff || (cp >= 0xd800 && cp <= 0xdfff))
            return null;
        out += String.fromCodePoint(cp);
        i += extra + 1;
    }
    return out;
}
function base64UrlEncode(bytes) {
    let out = "";
    for (let i = 0; i < bytes.length; i += 3) {
        const n = (bytes[i] << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0);
        out += B64URL[(n >> 18) & 63] + B64URL[(n >> 12) & 63];
        if (i + 1 < bytes.length)
            out += B64URL[(n >> 6) & 63];
        if (i + 2 < bytes.length)
            out += B64URL[n & 63];
    }
    return out;
}
function base64UrlDecode(s) {
    if (!/^[A-Za-z0-9_-]*$/.test(s) || s.length % 4 === 1)
        return null;
    const bytes = [];
    let buffer = 0;
    let bits = 0;
    for (const ch of s) {
        buffer = (buffer << 6) | B64URL.indexOf(ch);
        bits += 6;
        if (bits >= 8) {
            bits -= 8;
            bytes.push((buffer >> bits) & 0xff);
        }
    }
    return bytes;
}
/** base64url(JSON `{ts, key}`) — no padding, URL-safe. */
export function encodeAuditCursor(cursor) {
    return base64UrlEncode(utf8Encode(JSON.stringify({ ts: cursor.ts, key: cursor.key })));
}
/**
 * Decode and validate a cursor: `ts` must match {@link AUDIT_TIMESTAMP_RE} and
 * `key` must be 1..200 chars with no control characters. Returns `null` for
 * anything malformed, so the route answers 400 **before** either value reaches
 * a PostgREST filter.
 */
export function decodeAuditCursor(raw) {
    if (raw.length === 0 || raw.length > AUDIT_CURSOR_MAX)
        return null;
    const bytes = base64UrlDecode(raw);
    if (!bytes)
        return null;
    const json = utf8Decode(bytes);
    if (json === null)
        return null;
    let parsed;
    try {
        parsed = JSON.parse(json);
    }
    catch {
        return null;
    }
    const result = auditCursorSchema.safeParse(parsed);
    return result.success ? result.data : null;
}
//# sourceMappingURL=audit.js.map